/**
 * Safe localStorage wrapper that handles access denial errors
 * (e.g., in private browsing mode, iframe contexts, or when disabled by browser settings)
 */
export const safeLocalStorage = {
  /**
   * Safely retrieves an item from localStorage
   * @param key The key to retrieve
   * @returns The stored value or null if not found or access is denied
   */
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.warn(`localStorage.getItem('${key}') failed:`, e);
      return null;
    }
  },

  /**
   * Safely sets an item in localStorage
   * @param key The key to set
   * @param value The value to store
   */
  setItem: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.warn(`localStorage.setItem('${key}', '${value}') failed:`, e);
    }
  },

  /**
   * Safely removes an item from localStorage
   * @param key The key to remove
   */
  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn(`localStorage.removeItem('${key}') failed:`, e);
    }
  }
};

/**
 * Checks if localStorage is available and accessible
 * @returns true if localStorage can be used, false otherwise
 */
export const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = '__localStorage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};
